'use client';

import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { HStack, VStack, Box } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { Input } from '../../../../../components/styled/input';
import { IconButton } from '../../../../../components/icon-button';
import { Combobox } from '../../../../../components/combobox';
import { BadgeHttp } from '../../../../../components/styled/badge-http';
import {
  Search01Icon,
  ArrowUpDownIcon,
  Tick01Icon,
  Link02Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { createListCollection } from '@ark-ui/react';
import { Tooltip } from '../../../../../components/tooltip';
import type { ParsedEndpoint } from '../../ToolCallsRenderers/types';
import type { SearchAssetResult } from '../../../finding-search';
import { EndpointListItem } from './EndpointListItem';
import { MetaDefault, MonoSmall } from '../../../../../components/typography';

export type EndpointSelectorPreview = {
  url?: string;
  method?: string;
};
export type EndpointSelectorProps = {
  value: string | undefined;
  onChange: (endpointId: string | undefined) => void;
  endpoints?: ParsedEndpoint[];
  selectedPreview?: EndpointSelectorPreview;
  currentEndpointPlaceholder?: string;
  onSearchAssets?: (query: string) => Promise<SearchAssetResult[]>;
  disabled?: boolean;
};
export function EndpointSelector({
  value,
  onChange,
  endpoints = [],
  selectedPreview,
  currentEndpointPlaceholder,
  onSearchAssets,
  disabled,
}: EndpointSelectorProps) {
  const t = useTranslations('designSystem.chat.updateSignalModal');
  const [search, setSearch] = useState('');
  const [assetSearchResults, setAssetSearchResults] = useState<
    SearchAssetResult[]
  >([]);
  const [isSearchingAssets, setIsSearchingAssets] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const assetSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const endpointsWithSelected = useMemo(() => {
    if (!value) return endpoints;
    if (endpoints.some((endpoint) => endpoint.endpointId === value)) {
      return endpoints;
    }

    return [
      {
        endpointId: value,
        endpointUrl: selectedPreview?.url ?? value,
        path: selectedPreview?.url ?? value,
        method: selectedPreview?.method,
      },
      ...endpoints,
    ];
  }, [endpoints, value, selectedPreview]);
  const filtered = useMemo(() => {
    if (!search.trim()) return endpointsWithSelected;
    const query = search.toLowerCase();

    return endpointsWithSelected.filter(
      (endpoint) =>
        endpoint.path?.toLowerCase().includes(query) ||
        endpoint.endpointUrl?.toLowerCase().includes(query) ||
        endpoint.method?.toLowerCase().includes(query) ||
        endpoint.endpointDescription?.toLowerCase().includes(query),
    );
  }, [endpointsWithSelected, search]);
  const handleSelect = useCallback(
    (endpoint: ParsedEndpoint) => {
      if (disabled) return;
      onChange(endpoint.endpointId === value ? undefined : endpoint.endpointId);
    },
    [disabled, onChange, value],
  );
  const selectedAsset = useMemo((): SearchAssetResult | null => {
    if (!value) return null;

    return {
      id: value,
      url: selectedPreview?.url ?? value,
      method: selectedPreview?.method,
      type: 'ENDPOINT',
    };
  }, [value, selectedPreview?.url, selectedPreview?.method]);
  const handleAssetSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      if (assetSearchTimeout.current) {
        clearTimeout(assetSearchTimeout.current);
      }

      if (!onSearchAssets || !query.trim()) {
        setAssetSearchResults(selectedAsset ? [selectedAsset] : []);
        setIsSearchingAssets(false);

        return;
      }

      setIsSearchingAssets(true);
      assetSearchTimeout.current = setTimeout(async () => {
        try {
          const results = await onSearchAssets(query);

          setAssetSearchResults(
            results.filter((asset) => asset.type === 'ENDPOINT'),
          );
        } catch (error: unknown) {
          console.error('Failed to search endpoints', error);
          setAssetSearchResults([]);
        } finally {
          setIsSearchingAssets(false);
        }
      }, 300);
    },
    [onSearchAssets, selectedAsset],
  );

  useEffect(() => {
    return () => {
      if (assetSearchTimeout.current) {
        clearTimeout(assetSearchTimeout.current);
      }
    };
  }, []);
  useEffect(() => {
    if (!onSearchAssets || !selectedAsset) {
      if (!selectedAsset) {
        setAssetSearchResults([]);
      }

      return;
    }

    if (searchQuery.trim()) {
      return;
    }

    setAssetSearchResults((previous) => {
      const index = previous.findIndex(
        (asset) => asset.id === selectedAsset.id,
      );

      if (index === -1) {
        return previous.length === 0
          ? [selectedAsset]
          : [selectedAsset, ...previous];
      }

      const current = previous[index];

      if (
        current.url === selectedAsset.url &&
        current.method === selectedAsset.method
      ) {
        return previous;
      }

      return previous.map((asset, assetIndex) =>
        assetIndex === index ? selectedAsset : asset,
      );
    });
  }, [onSearchAssets, selectedAsset, searchQuery]);
  const assetsCollection = useMemo(
    () =>
      createListCollection({
        items: assetSearchResults.map((asset) => ({
          label: asset.url,
          value: asset.id,
          method: asset.method,
          type: asset.type,
        })),
      }),
    [assetSearchResults],
  );
  const selectedEndpoint = endpointsWithSelected.find(
    (endpoint) => endpoint.endpointId === value,
  );
  const displayUrl =
    selectedPreview?.url ||
    selectedEndpoint?.path ||
    selectedEndpoint?.endpointUrl;

  if (disabled) {
    return (
      <Box
        p={2.5}
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="md"
        bg="bg.subtle"
      >
        {displayUrl ? (
          <HStack gap={2} alignItems="center" minWidth={0}>
            {selectedPreview?.method ? (
              <BadgeHttp
                method={selectedPreview.method}
                size="sm"
                variant="emphasis"
                flexShrink={0}
                fontWeight="normal"
              >
                {selectedPreview.method}
              </BadgeHttp>
            ) : null}
            <Link02Icon
              size={12}
              style={{ flexShrink: 0, color: 'var(--colors-fg-subtle)' }}
            />
            <MonoSmall truncate minWidth={0}>
              {displayUrl}
            </MonoSmall>
          </HStack>
        ) : value ? (
          <HStack gap={2} alignItems="center" minWidth={0}>
            <Link02Icon
              size={12}
              style={{ flexShrink: 0, color: 'var(--colors-fg-subtle)' }}
            />
            <MonoSmall truncate minWidth={0}>
              {value}
            </MonoSmall>
          </HStack>
        ) : (
          <MetaDefault color="fg.muted">{t('endpointNotEditable')}</MetaDefault>
        )}
      </Box>
    );
  }

  if (onSearchAssets) {
    return (
      <Combobox.Root
        collection={assetsCollection}
        value={value ? [value] : []}
        onValueChange={(details) => {
          onChange(details.value[0] || undefined);
        }}
        onInputValueChange={(details) => {
          handleAssetSearch(details.inputValue);
        }}
        size="sm"
      >
        <Combobox.Control>
          <Combobox.Input
            placeholder={
              value
                ? t('typeToSearchEndpoints')
                : (currentEndpointPlaceholder ??
                  t('placeholders.selectEndpoint'))
            }
            asChild
          >
            <Input size="sm" />
          </Combobox.Input>
          <Combobox.Trigger asChild>
            <IconButton
              variant="ghost"
              aria-label={t('fields.endpoint')}
              size="xs"
            >
              <ArrowUpDownIcon size={14} />
            </IconButton>
          </Combobox.Trigger>
        </Combobox.Control>
        <Combobox.Positioner>
          <Combobox.Content maxH="300px" overflowY="auto">
            <Combobox.List>
              {isSearchingAssets ? (
                <Text fontSize="xs" color="fg.muted" p={2} textAlign="center">
                  {t('searchingEndpoints')}
                </Text>
              ) : assetsCollection.items.length > 0 ? (
                assetsCollection.items.map((item) => (
                  <Tooltip key={item.value} title={item.label}>
                    <Combobox.Item item={item}>
                      <VStack alignItems="stretch" gap={0.5} flex={1} minW={0}>
                        <HStack gap={2} flex={1} minW={0}>
                          {item.method ? (
                            <BadgeHttp
                              method={item.method}
                              size="sm"
                              variant="emphasis"
                              flexShrink={0}
                              fontWeight="normal"
                            >
                              {item.method}
                            </BadgeHttp>
                          ) : null}
                          <Combobox.ItemText
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            flex={1}
                          >
                            {item.label}
                          </Combobox.ItemText>
                        </HStack>
                        <MonoSmall color="fg.muted" truncate>
                          {item.value}
                        </MonoSmall>
                      </VStack>
                      <Combobox.ItemIndicator>
                        <Tick01Icon size={14} />
                      </Combobox.ItemIndicator>
                    </Combobox.Item>
                  </Tooltip>
                ))
              ) : (
                <Text fontSize="xs" color="fg.muted" p={2} textAlign="center">
                  {t('typeToSearchEndpoints')}
                </Text>
              )}
            </Combobox.List>
          </Combobox.Content>
        </Combobox.Positioner>
      </Combobox.Root>
    );
  }

  if (endpointsWithSelected.length === 0) {
    return (
      <Input
        size="sm"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value || undefined)}
        placeholder={currentEndpointPlaceholder ?? t('placeholders.endpointId')}
        fontFamily="mono"
        fontSize="xs"
      />
    );
  }

  return (
    <VStack gap={1.5} alignItems="stretch">
      <HStack
        gap={1.5}
        alignItems="center"
        px={2}
        py={1}
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="md"
        bg="bg.subtle"
      >
        <Search01Icon
          size={12}
          style={{ color: 'var(--colors-fg-muted)', flexShrink: 0 }}
        />
        <Input
          size="xs"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('placeholders.searchEndpoints')}
          border="none"
          outline="none"
          background="transparent"
          _focus={{ boxShadow: 'none' }}
          flex={1}
        />
      </HStack>

      <VStack
        gap={0.5}
        maxHeight="200px"
        overflowY="auto"
        alignItems="stretch"
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="md"
        p={1}
      >
        {filtered.length === 0 ? (
          <Box py={3} textAlign="center">
            <Text fontSize="xs" color="fg.muted">
              {t('noEndpointsFound')}
            </Text>
          </Box>
        ) : (
          filtered.map((endpoint, index) => (
            <EndpointListItem
              key={endpoint.endpointId ?? `ep-${index}`}
              endpoint={endpoint}
              isSelected={endpoint.endpointId === value}
              onSelect={handleSelect}
            />
          ))
        )}
      </VStack>
    </VStack>
  );
}
